
interface Permission {
    id: string;
    parent_id: string | null;
    entity: string;
    action: string | null;
    label: string;
    description: string;
    platform: string;
    created_at: string;
    updated_at: string | null;
    display_order: string | null;
    enabled: boolean;
    children: Permission[];
}

/**
 * Busca recursivamente un permiso por su action o entity en el array de permisos
 */
function hasPermission(permissions: Permission[], permissionKey: string): boolean {
    if (!permissions || !Array.isArray(permissions)) {
        return false;
    }

    return permissions.some((p: Permission) => {
        // Verificar si el permiso actual coincide por action o entity
        // Si action es null, usar entity como fallback
        const matches = (p.action === permissionKey || (p.action === null && p.entity === permissionKey)) && p.enabled;
        
        if (matches) {
            return true;
        }
        
        // Buscar recursivamente en children
        if (p.children && p.children.length > 0) {
            return hasPermission(p.children, permissionKey);
        }
        return false;
    });
}

/**
 * Verifica si el usuario tiene el permiso requerido desde localStorage
 */
function checkSystemPermission(requiresSystemPermission?: string | string[]): boolean {
    if (!requiresSystemPermission) {
        return true; // Si no requiere permiso, está habilitado
    }

    try {
        const permissionsUserStr = localStorage.getItem('permissionsUser');
        if (!permissionsUserStr) {
            return false; // Si no hay permisos en localStorage, no tiene acceso
        }

        const permissionsUser: Permission[] = JSON.parse(permissionsUserStr);
        
        // Si es un array de permisos, todos deben estar presentes
        if (Array.isArray(requiresSystemPermission)) {
            return requiresSystemPermission.every(permission => hasPermission(permissionsUser, permission));
        }
        
        // Si es un solo permiso
        return hasPermission(permissionsUser, requiresSystemPermission);
    } catch (error) {
        console.error('Error al verificar permisos:', error);
        return false; // En caso de error, no tiene acceso
    }
}

export interface menu {
    header?: string;
    title?: string;
    icon?: any;
    to?: any;
    chip?: string;
    BgColor?: string;
    chipBgColor?: string;
    chipColor?: string;
    chipVariant?: string;
    chipIcon?: string;
    children?: menu[];
    disabled?: boolean;
    type?: string;
    subCaption?: string;
    requiresPermission?: string | string[];
    beta?: boolean;
    short_name?: string;
    requiresSystemPermission?: string | string[]
}

// Definir los items sin calcular disabled (se calculará dinámicamente)
const sidebarItemBase: Omit<menu, 'disabled'>[] = [
    {
        short_name: 'mi_tablero',
        title: 'Mi Tablero',
        icon: 'screencast-2-linear',
        BgColor: 'secondary',
        to: { name: 'Mi Tablero' },
        requiresSystemPermission: 'my_board'
    },
    // {
    //     title: 'Mi Tablero 2',
    //     icon: 'screencast-2-linear',
    //     BgColor: 'primary',
    //     // requiresPermission: 'dashboard2',
    //     to: { name: 'Mi Tablero 2' }
    // },
    // {
    //     short_name: 'mis_ventas',
    //     title: 'Mis Ventas',
    //     icon: 'file-check-outline',
    //     BgColor: 'secondary',
    //     to: { name: 'Mis Ventas' },
    //     requiresSystemPermission: 'my_sales',
    // },
    { header: 'Mis Tareas' },
    {
        short_name: 'mis_folios',
        title: 'Mis Folios',
        icon: 'checklist-bold-duotone',
        BgColor: 'purpleComercialProject',
        to: { name: 'Mis Folios' },
        requiresSystemPermission: 'my_folios',
    },
    // {
    //     short_name: 'mis_cotizaciones',
    //     title: 'Mis Cotizaciones',
    //     icon: 'file-text-outline',
    //     BgColor: 'warning',
    //     to: { name: 'Mis Cotizaciones' },
    //     requiresSystemPermission: 'quote_by_work_order'
    // },
    // {
    //     short_name: 'autos_online',
    //     title: 'Autos Online',
    //     icon: 'spedometer-max-line-duotone',
    //     BgColor: 'greenProject',
    //     beta: false,
    //     requiresPermission: 'mc_autos',
    //     to: { name: 'Autos Online' },
    // },
    // {
    //     short_name: 'rc_contratista_online',
    //     title: 'RC Contratista Online',
    //     icon: 'sledgehammer-linear',
    //     BgColor: 'orangeProject',
    //     beta: false,
    //     requiresPermission: 'mc_rc_contratista',
    //     to: { name: 'RC Contratista Online' },

    // },
    // {
    //     short_name: 'gmm_online',
    //     title: 'GMM Online',
    //     icon: 'medical-kit-linear',
    //     BgColor: 'blueBin',
    //     beta: false,
    //     requiresPermission: 'mc_gmm',
    //     to: { name: 'GMM Online' },
    // },
    // {
    //     short_name: 'camiones_individual',
    //     title: 'Camiones Online',
    //     icon: 'bus-linear',
    //     BgColor: 'blueBin',
    //     beta: false,
    //     requiresPermission: 'mc_c_individual',
    //     to: { name: 'Camiones Online' }
    // },
    { header: 'Mi Cartera' },
    {
        short_name: 'mis_clientes',
        title: 'Mis Clientes',
        icon: 'user-id-outline',
        BgColor: 'purpleComercialProject',
        to: { name: 'Mis Clientes' },
        requiresSystemPermission: 'my_clients',
    },
    {
        short_name: 'mis_polizas',
        title: 'Mis Pólizas',
        icon: 'file-check-outline',
        BgColor: 'secondary',
        to: { name: 'Mis Pólizas' },
        requiresSystemPermission: 'my_policies',
    },
    {
        short_name: 'mis_recibos_pendientes',
        title: 'Mis Recibos Pendientes',
        icon: 'bill-list-outline',
        BgColor: 'greenProject',
        to: { name: 'Mis Recibos Pendientes' },
        requiresSystemPermission: 'my_pending_tickets',
    },
    {
        short_name: 'mis_renovaciones',
        title: 'Mis Renovaciones',
        icon: 'file-remove-outline',
        BgColor: 'orangeProject',
        to: { name: 'Mis Renovaciones' },
        requiresSystemPermission: 'my_renovations',
    },
    /*    {
        title: 'Mis Claves de Agente',
        icon: 'key-minimalistic-square-2-outline',
        BgColor: 'warning',
        to: { name: 'Mis Claves de Agente' }
    }, */
    // {
    //     short_name: 'soporte',
    //     title: 'Soporte',
    //     icon: 'move-to-folder-broken',
    //     BgColor: 'info',
    //     to: { name: 'Tickets de Soporte' },
    //     requiresSystemPermission: 'support'
    // },
    {
        short_name: 'folders',
        title: 'Carpetas',
        icon: 'folder-outline',
        BgColor: 'info',
        to: { name: 'Folders List' },
        // requiresSystemPermission: 'folder'
    }
];

// Función que retorna los items con disabled calculado dinámicamente
export function getSidebarItems(): menu[] {
    
    const items = sidebarItemBase.map((item) => {
        if (item.header) {
            return item as menu; // Los headers no necesitan disabled
        }
        
        // Calcular disabled dinámicamente basándose en permisos
        const disabled = item.requiresSystemPermission 
            ? !checkSystemPermission(item.requiresSystemPermission)
            : false;
        
        return {
            ...item,
            disabled
        } as menu;
    });
    
    return items;
}

// Exportar también los items base para uso en getEnableFirstRoute
export { sidebarItemBase };

// Exportar como función para que se calcule dinámicamente cada vez que se accede
// Esto asegura que disabled se calcule con los permisos actuales
const sidebarItem = getSidebarItems();
export default sidebarItem;
